import AppConstants from "../constants/AppConstants";
import FileUtils from "./FileUtils";
import config from "../config/app";
import { getExternalPreviewURL, isExternalAssetKey } from "./ExternalAssetUtils";
import _ from "lodash";

const AssetUtils = {
  /**
   * Builds a preview image URL using the v2 URL structure, which serves an
   * optimized webp image.
   *
   * @param {string} key - The asset key.
   * @param {Object} [options]
   * @param {string} [options.assetUrl] - Media server base URL.
   * @param {import("../constants/AssetProviderConstants").AssetProviderValue} [options.provider] - The content's `provider`.
   * @returns {string} Preview image URL.
   */
  getV2PreviewURL(key, { assetUrl = config.assetURL, provider } = {}) {
    if (isExternalAssetKey(key)) {
      return getExternalPreviewURL(key, provider);
    }

    const ext = FileUtils.getExt(key);

    if (ext === AppConstants.EXT_WOFF || ext === AppConstants.EXT_WOFF2) {
      return `${assetUrl}/static/woff.png`;
    }

    const [assetId, userIdOrShared, filename] = key.split("/");

    return `${assetUrl}/v2/${userIdOrShared}/${assetId}/${filename}/preview`;
  },

  /**
   * @deprecated Use {@link AssetUtils.getV2PreviewURL} instead, which uses
   * the v2 URL structure.
   */
  getPreviewURL(key, asset, assetUrl = config.assetURL) {
    if (isExternalAssetKey(key)) {
      return getExternalPreviewURL(key, asset?.provider);
    }

    const ext = FileUtils.getExt(key);

    if (AssetUtils.isLottieAsset(asset) || ext === AppConstants.EXT_LOTTIE) {
      return `${assetUrl}/preview/${key}`;
    }

    if (ext === AppConstants.EXT_WOFF || ext === AppConstants.EXT_WOFF2) {
      return `${assetUrl}/static/woff.png`;
    }

    var url = `${assetUrl}/preview/${key}`;
    if (_.indexOf(AppConstants.ASSET_IMAGE_ONLY_EXTS, ext) < 0) {
      url = `${url}.png`;
    }
    return url;
  },

  /**
   * Whether this asset type requires an async image preview with loading state,
   * rather than a simple CSS background-image.
   * @param {Object} asset
   * @returns {boolean}
   */
  hasAsyncPreview(asset) {
    return AssetUtils.isLottieAsset(asset);
  },

  /**
   * Returns a static fallback URL for asset types that have async previews.
   * Used when the dynamic preview fails to load.
   * @param {Object} asset
   * @param {string} [assetUrl]
   * @returns {string|null}
   */
  getFallbackURL(asset, assetUrl = config.assetURL) {
    if (AssetUtils.isLottieAsset(asset)) {
      return `${assetUrl}/static/lottie.png`;
    }
    return null;
  },

  getOriginalURL(key, assetUrl = config.assetURL) {
    return `${assetUrl}/file/${key}`;
  },

  /**
   * Checks whether an asset is a Lottie animation by extension or asset_type.
   * Extensions in ASSET_TYPE_LOTTIE_EXTS (e.g. .lottie) are always Lottie.
   * JSON files may be reclassified to ASSET_TYPE_LOTTIE by the backend.
   * @param {Object} asset
   * @returns {boolean}
   */
  isLottieAsset(asset) {
    if (!asset) return false;
    return (
      AppConstants.ASSET_TYPE_LOTTIE_EXTS.includes(asset.ext) ||
      asset.asset_type === AppConstants.ASSET_TYPE_LOTTIE
    );
  },

  getElementType(asset) {
    var type = AppConstants.ELEMENT_TYPE_SIMPLE;
    if (AssetUtils.isLottieAsset(asset)) {
      type = AppConstants.ELEMENT_TYPE_LOTTIE;
    } else if (AppConstants.ASSET_TYPE_IMAGE == asset.asset_type) {
      if (AppConstants.EXT_SVG == asset.ext) {
        type = AppConstants.ELEMENT_TYPE_SVG;
      } else {
        type = AppConstants.ELEMENT_TYPE_GRAPHIC;
      }
    } else if (AppConstants.ASSET_TYPE_VIDEO == asset.asset_type) {
      type = AppConstants.ELEMENT_TYPE_VIDEO;
    }

    return type;
  },

  //only get the asset properties that is relevant for the element
  getElementAsset(asset) {
    var elemAsset = _.pick(asset, ["key", "aspect_ratio"]);
    elemAsset.title = _.get(asset, "asset_name");
    elemAsset.type = "asset";

    if (elemAsset.aspect_ratio == null && AssetUtils.isLottieAsset(asset)) {
      elemAsset.aspect_ratio = 1;
    }

    if (AssetUtils.isLottieAsset(asset)) {
      elemAsset.loop = 1;
    }

    return elemAsset;
  },

  //only get the asset properties that is relevant for the element
  getGalleryImageAsset(asset) {
    var elemAsset = { key: _.get(asset, "key") };
    elemAsset.ar = _.get(asset, "aspect_ratio");
    elemAsset.title = _.get(asset, "asset_name");
    return elemAsset;
  },

  getNameFromKey(key) {
    return _.last(key.split("/"));
  },
};

export default AssetUtils;
